import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import type { 
  SubscriptionPlan, 
  UserSubscriptionStatus,
  SubscribeRequest,
  UpgradeRequest,
  CancelRequest 
} from '@/types/billing';

// Hook to get visible subscription plans
export function useSubscriptionPlans(userId?: string) {
  return useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans', userId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-plans/plans`,
        {
          headers: {
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription plans');
      }

      return response.json();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to get referral-specific subscription plans
export function useReferralPlans() {
  return useQuery<SubscriptionPlan[]>({
    queryKey: ['referral-plans'],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-plans/referral-plans`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch referral plans');
      }

      return response.json();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to get user's subscription status
export function useUserSubscriptionStatus() {
  return useQuery<UserSubscriptionStatus>({
    queryKey: ['user-subscription-status'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-plans/status`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription status');
      }

      return response.json();
    },
    enabled: true, // Enable subscription status checking
  });
}

// Hook to subscribe to a plan
export function useSubscribe() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, SubscribeRequest>({
    mutationFn: async (subscribeData) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management/subscribe`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subscribeData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to subscribe');
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      
      toast.success('Successfully subscribed to plan!');
      return data;
    },
    onError: (error) => {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to subscribe');
    },
  });
}

// Hook to upgrade subscription
export function useUpgradeSubscription() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpgradeRequest>({
    mutationFn: async (upgradeData) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management/upgrade`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(upgradeData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to upgrade subscription');
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      
      toast.success('Successfully upgraded subscription!');
      return data;
    },
    onError: (error) => {
      console.error('Upgrade error:', error);
      toast.error(error.message || 'Failed to upgrade subscription');
    },
  });
}

// Hook to cancel subscription
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CancelRequest>({
    mutationFn: async (cancelData) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management/cancel`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cancelData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      
      toast.success('Subscription cancelled successfully');
      return data;
    },
    onError: (error) => {
      console.error('Cancel error:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    },
  });
}

// Hook to reactivate subscription
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-management/reactivate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reactivate subscription');
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      
      toast.success('Subscription reactivated successfully!');
      return data;
    },
    onError: (error) => {
      console.error('Reactivate error:', error);
      toast.error(error.message || 'Failed to reactivate subscription');
    },
  });
}

// Types for referral signup response
type ReferralSignupResponse =
  | {
      success: true;
      user: { id: string; email: string | null };
      plan: { name: string; identifier: string; requires_payment: boolean };
      checkout?: { url: string | null; session_id: string };
      redirect_url?: string;
    }
  | { error: string; details?: string };

// Hook to create user and subscribe (for external API usage)
export function useCreateUserAndSubscribe() {
  return useMutation<ReferralSignupResponse, Error, { email: string; password: string; fullName?: string; planIdentifier: string; paymentMethodId?: string; affiliateRef?: string; promoCode?: string; rewardfulReferral?: string; trialPeriodDays?: number }>({
    mutationFn: async (userData) => {
      const { data, error } = await supabase.functions.invoke('referral-signup', {
        body: userData,
      });

      if (error) {
        throw new Error(error.message || 'Failed to create user and subscribe');
      }

      return data as ReferralSignupResponse;
    },
    onSuccess: (data) => {
      console.log('referral-signup result:', data);
      
      // Check if it's a successful response
      if ('success' in data && data.success) {
        // Handle paid plans
        if (data.plan.requires_payment) {
          if (data.checkout?.url) {
            // Set flag to prevent global redirects from interfering
            if (typeof window !== 'undefined') {
              localStorage.setItem('pendingCheckout', '1');
            }
            console.log('Redirecting to Stripe checkout:', data.checkout.url);
            window.location.assign(data.checkout.url);
            return;
          }
          // Server didn't return checkout.url for paid plan - this is a bug
          toast.error('Payment is required, but no Checkout URL was returned.');
          console.error('Expected checkout.url for paid plan. Payload:', data);
          if (typeof window !== 'undefined') {
            localStorage.removeItem('pendingCheckout');
          }
          return;
        }
        
        // Handle free plans with redirect URL
        if (data.redirect_url) {
          console.log('Redirecting to:', data.redirect_url);
          window.location.assign(data.redirect_url);
          return;
        }
        
        // Default success message for free plans without redirect_url
        toast.success('Account created successfully!');
        if (typeof window !== 'undefined') {
          localStorage.removeItem('pendingCheckout');
        }
      } else if ('error' in data) {
        // Handle error response
        toast.error(data.error || 'Failed to create user');
        if (typeof window !== 'undefined') {
          localStorage.removeItem('pendingCheckout');
        }
      }
      return data;
    },
    onError: (error) => {
      console.error('Create user and subscribe error:', error);
      toast.error(error.message || 'Failed to create user and subscribe');
      if (typeof window !== 'undefined') {
        localStorage.removeItem('pendingCheckout');
      }
    },
  });
}